import React, { useEffect } from "react";
import styled from "styled-components";
import qs from "qs";
import { useTranslation, Trans } from "@lib/i18n";
import { string, bool, number, func } from "prop-types";

import RequestButton from "@components/shared/RequestButton";
import CollapsableContainer from "@components/shared/CollapsableContainer";
import Typography from "@components/shared/Typography";

import { tablet } from "./shared/utils";
import { basketPersonalContinueEcommerceEvent } from "@gtm/events/basket";
import { getBrandIdLoginRedirect } from "@lib/getBrandIdLoginRedirect";
import { toast } from "@lib/reactToastify";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import gtm from "@gtm/core";
import { useBrandIdSettingsSelector } from "@redux/reducers/appConfig";
import { getUrlWithoutTrailingSlash } from "@shared/v2/lib/getUrlWithoutTrailingSlash";
import { redirectToExternalURL } from "@shared/v2/lib/redirectToExternalURL";
import { usePrimaryColor } from "@hooks/usePrimaryColor";

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  flex-direction: column;
  align-items: center;

  ${tablet`
    justify-content: space-between;
  `}
`;

const StyledTypography = styled(Typography)`
  margin: 1rem 0;
`;

const RedirectBrandIdForms = ({
  redirectRegistration,
  redirectLogin,
  disabled,
  isCurrentStep,
  isOpen,
  defaultOpen,
  gtmEvent,
  index,
  registrationType = null,
  isHeadTextHidden = false,
  inverted = false,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const primaryColor = usePrimaryColor();
  const isB2BSelected = useIsB2BSelected();
  const brandIDConfig = useBrandIdSettingsSelector();

  useEffect(() => {
    if (isOpen && gtmEvent) {
      gtmEvent();
    }
  }, [isOpen]);

  const onRegisterButtonClick = async () => {
    let brandIdQuery = {
      redirectUrl: getUrlWithoutTrailingSlash(brandIDConfig.registrationUrl),
      return_to: `${getUrlWithoutTrailingSlash(
        brandIDConfig.dealApiUrl
      )}/brandid/landing-page`,
      location: redirectRegistration,
    };

    if (registrationType) {
      gtm.fire(
        basketPersonalContinueEcommerceEvent({
          personalOption: "Create account",
          isB2BSelected,
        })
      );

      brandIdQuery.creation = registrationType;
    }

    window.location.href = `${
      brandIDConfig.dealApiUrl
    }/user/register${qs.stringify(brandIdQuery, {
      addQueryPrefix: true,
      skipNulls: true,
      encode: false,
    })}`;
  };

  const onLoginButtonClick = () => {
    if (registrationType) {
      gtm.fire(
        basketPersonalContinueEcommerceEvent({
          personalOption: "Login",
          isB2BSelected,
        })
      );
    }

    return redirectToExternalURL(
      getBrandIdLoginRedirect(redirectLogin, brandIDConfig, language)
    );
  };

  const onRequestError = error => {
    toast.error(
      t("notify.apiDefaultError"),
      {
        toastId: "brandid-registration-redirect-error",
      },
      error
    );
  };

  return (
    <CollapsableContainer
      title={t("basket.personalDetails.selectTitle")}
      titleIndex={index}
      titleAsHeader={isCurrentStep}
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      isDisabled={disabled}
      isHeadTextHidden={isHeadTextHidden}
    >
      <Container>
        <StyledTypography size="sm">
          <Trans i18nKey="myDetails.redirectContainer.loginCreateAccount" />
        </StyledTypography>
        <StyledTypography size="sm">
          {t("myDetails.redirectContainer.existingAccount")}
        </StyledTypography>
        <RequestButton
          withBorder
          color={primaryColor}
          inverted={inverted}
          data-id="basket-redirect-registration-button"
          triggerRequestCallback={onRegisterButtonClick}
          onRequestError={onRequestError}
        >
          {t("myDetails.redirectContainer.register")}
        </RequestButton>
        <RequestButton
          withBorder
          color={primaryColor}
          inverted={inverted}
          data-id="basket-redirect-login-button"
          triggerRequestCallback={onLoginButtonClick}
        >
          {t("myDetails.redirectContainer.login")}
        </RequestButton>
      </Container>
    </CollapsableContainer>
  );
};

RedirectBrandIdForms.propTypes = {
  redirectRegistration: string.isRequired,
  redirectLogin: string.isRequired,
  disabled: bool.isRequired,
  isCurrentStep: bool,
  defaultOpen: bool,
  isOpen: bool,
  gtmEvent: func,
  index: number,
  registrationType: number,
  isHeadTextHidden: bool,
  inverted: bool,
};

export default RedirectBrandIdForms;
